import { getIndustryGroup, BusinessTypeGroups } from "./BusinessTypes";

// Stable identifiers for every dashboard sidebar item. These are persisted in
// dashboardNavItemSettings rows and in preset definitions, and are the shared
// identity primitive for both the 3-view structure (admin/working split) and
// per-school nav customization — never rename a key once shipped (orphaned rows
// are ignored, but the customization is lost).

export const NAV_ITEM_KEYS = [
  "activity",
  "calendar",
  "calendar-london-lady",
  "reports-london-lady",
  "services",
  "services.listings",
  "services.bundles",
  "services.products",
  "services.invoices",
  "services.basket-orders",
  "services.gift-cards",
  "services.discounts",
  "services.affiliates",
  "memberships",
  "memberships.plans",
  "memberships.members",
  "ai-chatbot",
  "embeds",
  "embeds.sharing-links",
  "embeds.components",
  "embeds.settings",
  "customers",
  "customers.list",
  "customers.newsletters",
  "customers.waiting-list",
  "customers.reviews",
  // Marketing group. "services.affiliates" and "customers.newsletters" live
  // under it in the catalog — they keep their original keys (see header note).
  "marketing",
  "marketing.loyalty",
  "marketing.discovery",
  "data-model",
  "website",
  "website.pages",
  "website.branding",
  "website.navigation",
  "website.seo",
  "website.analytics",
  "website.social",
  "website-v2",
  "website-v2.pages",
  "notifications",
  "notifications.dispatch-center",
  "notifications.triggers",
  "notifications.workflows",
  "notifications.templates",
  "notifications.settings",
  "automations",
  "income",
  "income.transactions",
  "income.monthly",
  "income.bookings",
  "income.vat",
  "business",
  "business.stripe",
  "business.billing",
  "business.team",
  "business.time-off",
  "settings",
  // The /settings hub (plans/2026-10-26-dashboard-redesign.md, Phase 1C).
  "settings.all",
  "settings.general",
  "settings.locations",
  "settings.add-ons",
  "settings.keywords",
  "settings.checkout",
  "settings.event-reviews",
  "settings.terms",
  "settings.booking-policies",
  "settings.newsletter",
  "settings.reminders",
  "settings.waiting-lists",
  "settings.resource-pools",
  "referrals",
  // Explore Baluu: module On/Off (plans/2026-10-26-dashboard-redesign.md).
  "explore",
  // Today: the new sidebar's home page (same plan, Phase 3).
  "today",
  "support"
] as const;

export type NavItemKey = (typeof NAV_ITEM_KEYS)[number];

// Settings pages that aren't sidebar items (so have no nav key) but can still
// be pinned as a shortcut: the dashboard's destinations registry
// (lib/navConfig/destinations.ts) declares them with these ids. Persisted in
// dashboardShortcut rows, so never rename one once shipped.
export const SETTINGS_DESTINATION_IDS = [
  "settings.profile",
  "settings.bank",
  "settings.delivery-options",
  "settings.product-reviews",
  "settings.ai-chatbot",
  "settings.ai-chatbot-instructions",
  "settings.ai-credits",
  // settings.plan and settings.plans were merged into Plan & billing on
  // 2026-11-04; kept so stored pins still resolve (RETIRED_SHORTCUT_IDS).
  "settings.plan",
  "settings.plans",
  "settings.dashboard"
] as const;

export type SettingsDestinationId = (typeof SETTINGS_DESTINATION_IDS)[number];

/** Everything a shortcut can point at: a sidebar page or a settings page. */
export const SHORTCUT_KEYS: readonly string[] = [...NAV_ITEM_KEYS, ...SETTINGS_DESTINATION_IDS];

/** The most shortcuts one list (a user's own, or a school's default) holds. */
export const MAX_SHORTCUTS = 30;

// Saved views on the shared record table ("Views ▾"): the list pages that
// have them, the cap per person per page, and how a view is pinned as a
// shortcut (`view:<id>`, next to the nav keys above).
export const SAVED_VIEW_PAGES = ["bookings", "customers", "listings"] as const;
export type SavedViewPage = (typeof SAVED_VIEW_PAGES)[number];
export const MAX_SAVED_VIEWS = 50;
export const SAVED_VIEW_NAME_MAX = 60;
export const SAVED_VIEW_QUERY_MAX = 2000;
const SAVED_VIEW_SHORTCUT_PREFIX = "view:";
export const savedViewShortcutKey = (id: string) => `${SAVED_VIEW_SHORTCUT_PREFIX}${id}`;
/** The view id in a `view:<id>` shortcut key, or null for any other key. */
export function savedViewIdFromShortcutKey(key: string): string | null {
  if (!key.startsWith(SAVED_VIEW_SHORTCUT_PREFIX)) return null;
  const id = key.slice(SAVED_VIEW_SHORTCUT_PREFIX.length);
  return /^[a-z0-9]{1,40}$/.test(id) ? id : null;
}
export const isSavedViewPage = (page: string): page is SavedViewPage =>
  (SAVED_VIEW_PAGES as readonly string[]).includes(page);

// Items a school can never hide: they must always be able to reach help and the
// place where customization (and the Junno layout) is undone. `settings.general`
// is locked too — it hosts the layout toggle + general business settings — so the
// Settings group can never be emptied of its entry point to those controls.
export const LOCKED_VISIBLE_NAV_KEYS: NavItemKey[] = [
  "settings",
  "settings.general",
  "support"
];

// The two structural views of the dashboard (3-view rebrand). Welcome (the
// launcher at "/") has no nav of its own, so it is not a view key here.
export type DashboardView = "admin" | "working";

export type DashboardPresetKey =
  | "general"
  | "yoga-fitness"
  | "tours-activities"
  | "courses-workshops";

export interface DashboardPreset {
  key: DashboardPresetKey;
  label: string;
  description: string;
  hiddenKeys: NavItemKey[];
}

// Starter values — deliberately conservative (hiding too little beats hiding
// something a school needs). Product-tunable in this one place.
export const DASHBOARD_PRESETS: DashboardPreset[] = [
  {
    key: "general",
    label: "General",
    description: "Everything on. The full dashboard.",
    hiddenKeys: []
  },
  {
    key: "yoga-fitness",
    label: "Yoga & fitness studio",
    description: "Classes, memberships and customers front and centre.",
    hiddenKeys: ["services.products", "services.invoices"]
  },
  {
    key: "tours-activities",
    label: "Tours & activities",
    description: "Bookings and availability first; no membership plans.",
    hiddenKeys: ["memberships", "services.invoices"]
  },
  {
    key: "courses-workshops",
    label: "Courses & workshops",
    description: "Course listings and bookings; no membership plans or products.",
    hiddenKeys: ["memberships", "services.products"]
  }
];

export function getDashboardPreset(key: string): DashboardPreset | undefined {
  return DASHBOARD_PRESETS.find(p => p.key === key);
}

// Conservative mapping from the onboarding industry taxonomy to a dashboard
// preset, so a new school's sidebar is decluttered to match its vertical. Derived
// from the canonical industry GROUP (8 groups → 4 presets) via getIndustryGroup,
// so it stays in lockstep with BusinessTypeToGroup instead of re-listing every
// type. Unmapped groups (beauty_personal_care, pet_services, other) fall back to
// "general" (no decluttering).
const GROUP_TO_PRESET: Partial<Record<BusinessTypeGroups, DashboardPresetKey>> = {
  [BusinessTypeGroups.fitness_wellness]: "yoga-fitness",
  [BusinessTypeGroups.sports_recreation]: "yoga-fitness",
  [BusinessTypeGroups.performing_arts]: "courses-workshops",
  [BusinessTypeGroups.education_enrichment]: "courses-workshops",
  [BusinessTypeGroups.entertainment_leisure]: "tours-activities"
};

export function presetForBusinessType(
  businessType?: string | null
): DashboardPresetKey {
  const group = getIndustryGroup(businessType);
  if (!group) return "general";
  return GROUP_TO_PRESET[group] ?? "general";
}
